
import { useEffect, useRef } from 'react';

interface Office {
  city: string;
  lat: number;
  lng: number;
  popupHtml: string;
}

const OFFICES: Office[] = [
  {
    city: 'Paris',
    lat: 48.8566,
    lng: 2.3522,
    popupHtml: `
      <div style="font-family:sans-serif;min-width:180px">
        <div style="font-weight:700;font-size:14px;color:#0f172a;margin-bottom:4px">🇫🇷 Paris</div>
        <div style="font-weight:600;font-size:13px;color:#14B8A6">ITEC Groupe</div>
        <div style="font-size:12px;color:#64748b;margin-top:2px">14 rue Beffroy</div>
        <div style="font-size:12px;color:#64748b">92200 Neuilly-sur-Seine, France</div>
      </div>`,
  },
  {
    city: 'Lyon',
    lat: 45.7640,
    lng: 4.8357,
    popupHtml: `
      <div style="font-family:sans-serif;min-width:180px">
        <div style="font-weight:700;font-size:14px;color:#0f172a;margin-bottom:4px">🇫🇷 Lyon</div>
        <div style="font-weight:600;font-size:13px;color:#14B8A6">ITEC Groupe</div>
        <div style="font-size:12px;color:#64748b;margin-top:2px">Agence Lyon</div>
      </div>`,
  },
  {
    city: 'Tunis',
    lat: 36.8065,
    lng: 10.1815,
    popupHtml: `
      <div style="font-family:sans-serif;min-width:180px">
        <div style="font-weight:700;font-size:14px;color:#0f172a;margin-bottom:4px">🇹🇳 Tunis</div>
        <div style="font-weight:600;font-size:13px;color:#14B8A6">ITEC Tunisie</div>
        <div style="font-size:12px;color:#64748b;margin-top:2px">Centre de services et ingénierie</div>
      </div>`,
  },
  {
    city: 'Dubai',
    lat: 25.2048,
    lng: 55.2708,
    popupHtml: `
      <div style="font-family:sans-serif;min-width:200px">
        <div style="font-weight:700;font-size:14px;color:#0f172a;margin-bottom:4px">🇦🇪 Dubai</div>
        <div style="font-weight:600;font-size:13px;color:#14B8A6">ITEC Systems &amp; Software Consulting - FZCO</div>
        <div style="font-size:12px;color:#64748b;margin-top:2px">Dubai, UAE</div>
      </div>`,
  },
];

interface LeafletMapProps {
  className?: string;
}

export default function LeafletMap({ className = '' }: LeafletMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Dynamically import Leaflet to avoid SSR issues
    import('leaflet').then((L) => {
      // Fix default icon paths broken by bundlers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      // Custom teal pin icon
      const pinIcon = L.divIcon({
        className: '',
        html: `<div style="
          width:32px;height:32px;
          background:#14B8A6;
          border:3px solid #fff;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          box-shadow:0 2px 8px rgba(0,0,0,0.25);
        "></div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -36],
      });

      const map = L.map(mapRef.current!, {
        center: [30, 20],
        zoom: 2,
        scrollWheelZoom: false,
        zoomControl: true,
      });

      mapInstanceRef.current = map;

      // OpenStreetMap tile layer — no API key needed
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright" rel="nofollow" target="_blank">OpenStreetMap</a> contributors',
        maxZoom: 18,
      }).addTo(map);

      // Add markers with popups
      OFFICES.forEach((office) => {
        L.marker([office.lat, office.lng], { icon: pinIcon })
          .addTo(map)
          .bindPopup(office.popupHtml, {
            maxWidth: 260,
            className: 'itec-popup',
          });
      });
    });

    return () => {
      if (mapInstanceRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (mapInstanceRef.current as any).remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {/* Leaflet CSS */}
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
        crossOrigin=""
      />
      <style>{`
        .itec-popup .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          border: 1px solid #e2e8f0;
          padding: 0;
        }
        .itec-popup .leaflet-popup-content {
          margin: 12px 14px;
        }
        .itec-popup .leaflet-popup-tip {
          background: #fff;
        }
      `}</style>
      <div
        ref={mapRef}
        className={className}
        style={{ width: '100%' }}
      />
    </>
  );
}